import { SORTER_CAPACITY, type SorterInput, type SorterState, sorterPourable } from "@playpal/core";
import { useEffect } from "react";

/**
 * Eight colours a nine-year-old can tell apart at arm's length on a phone.
 *
 * Named as well as coloured, because "pour the red one into the red one" is how
 * children talk about this game to each other, and because a name is the only
 * thing a screen reader can read out. Each also carries a little symbol, drawn
 * faintly on every band, so two colours that look alike to colour-blind eyes
 * still never look the same.
 */
const COLOURS = [
	{ name: "red", fill: "bg-red-500", symbol: "♥" },
	{ name: "blue", fill: "bg-blue-500", symbol: "▲" },
	{ name: "green", fill: "bg-green-500", symbol: "●" },
	{ name: "yellow", fill: "bg-yellow-400", symbol: "★" },
	{ name: "purple", fill: "bg-purple-500", symbol: "◆" },
	{ name: "orange", fill: "bg-orange-500", symbol: "✚" },
	{ name: "pink", fill: "bg-pink-400", symbol: "✿" },
	{ name: "brown", fill: "bg-amber-700", symbol: "■" },
] as const;

/** Square, and the tubes are laid out to suit whatever shape that gives them. */
export const SORTER_ASPECT = 1;

interface Props {
	state: SorterState;
	onPress: (input: SorterInput) => void;
	live: boolean;
}

/**
 * The tubes.
 *
 * Unlike the other three, this board is still between one tap and the next, so
 * it can say what is on it: every tube is a button whose name is its contents,
 * bottom first, which is both what a screen reader needs and the only way this
 * game is playable without sight of it.
 */
export function SorterField({ state, onPress, live }: Props) {
	// Two rows once there are more than five, so a tube never gets so narrow on
	// a phone that a child cannot tell one band of colour from the next.
	const perRow = state.tubes.length > 5 ? Math.ceil(state.tubes.length / 2) : state.tubes.length;

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div
				className="grid w-full gap-2 p-2"
				style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
			>
				{state.tubes.map((tube, index) => (
					<Tube
						// The tubes are a fixed set for the level, so their place in it
						// really is what identifies them.
						key={index}
						tube={tube}
						index={index}
						picked={state.picked === index}
						// Dimmed when it is not somewhere the held colour can go, which
						// is the one hint this game gives and the one it needs.
						reachable={
							state.picked === null || sorterPourable(state.tubes, state.picked, index) > 0
						}
						onPress={() => onPress({ tap: index })}
						live={live}
					/>
				))}
			</div>
		</div>
	);
}

function Tube({
	tube,
	index,
	picked,
	reachable,
	onPress,
	live,
}: {
	tube: readonly number[];
	index: number;
	picked: boolean;
	reachable: boolean;
	onPress: () => void;
	live: boolean;
}) {
	const contents = tube.length
		? tube.map((colour) => COLOURS[colour - 1]?.name ?? "?").join(", ")
		: "empty";

	// A finished tube: full, and all one colour. It gets its little moment.
	const done = tube.length === SORTER_CAPACITY && tube.every((colour) => colour === tube[0]);

	return (
		<button
			type="button"
			disabled={!live}
			aria-pressed={picked}
			aria-label={`Tube ${index + 1}: ${contents}`}
			onPointerDown={(event) => {
				event.preventDefault();
				if (live) onPress();
			}}
			className={`relative touch-none select-none transition ${
				picked ? "z-10 -translate-y-3 scale-[1.04]" : ""
			} ${reachable ? "" : "opacity-40"}`}
			style={{ aspectRatio: `1 / ${SORTER_CAPACITY * 0.9}` }}
		>
			{/* The glass. Everything visual lives on this span so the button can
			    stay a plain, well-labelled button. */}
			<span
				aria-hidden="true"
				className={`absolute inset-0 flex flex-col-reverse overflow-hidden rounded-b-[1.4rem] rounded-t-lg border-2 bg-white/5 p-[3px] pt-1.5 shadow-inner transition ${
					picked
						? "border-white bg-white/15 shadow-lg"
						: done
							? "border-white/70 animate-glow"
							: "border-white/30"
				}`}
			>
				{Array.from({ length: SORTER_CAPACITY }, (_, slot) => {
					const colour = tube[slot];
					const paint = colour ? COLOURS[colour - 1] : undefined;
					// The surface of a pour: the topmost band of each run of colour
					// gets the meniscus curve; bands buried under their own colour
					// stay flat so a run reads as one body of liquid.
					const surface = colour !== 0 && tube[slot + 1] !== colour;
					return (
						<span
							// Keyed on the colour as well as the slot, so a band that
							// changes colour is a fresh element — and a fresh element
							// runs the rise-in animation, which is the pour.
							key={`${slot}-${colour ?? 0}`}
							className={`relative block flex-1 ${
								paint
									? `liquid ${paint.fill} animate-liquid-in ${surface ? "rounded-t-[50%_30%]" : ""}`
									: "bg-transparent"
							}`}
						>
							{paint && (
								<span className="absolute inset-0 grid place-items-center text-[0.55rem] font-bold text-white/40">
									{paint.symbol}
								</span>
							)}
						</span>
					);
				})}

				{/* The highlight streak that makes the glass glassy. */}
				<span className="pointer-events-none absolute inset-y-[5%] left-[10%] w-[9%] rounded-full bg-white/25" />
				{/* The rim: a brighter lip across the mouth of the tube. */}
				<span className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-lg bg-white/30" />
			</span>

			{/* A sparkle over a finished tube — praise, but quiet praise. */}
			{done && (
				<span
					aria-hidden="true"
					className="animate-pop pointer-events-none absolute -top-3 left-1/2 -translate-x-1/2 text-sm"
				>
					✨
				</span>
			)}
		</button>
	);
}

/** Starting the level again, and the number-keys shortcut for a laptop. */
export function SorterControls({
	state,
	onPress,
	live,
}: {
	state: SorterState;
	onPress: (input: SorterInput) => void;
	live: boolean;
}) {
	useNumberKeys(state.tubes.length, onPress, live);

	return (
		<div className="flex items-center justify-center gap-4 py-2">
			<p className="text-base font-semibold opacity-70">
				Level {state.level + 1} · {state.moves} {state.moves === 1 ? "pour" : "pours"}
			</p>
			<button
				type="button"
				onPointerDown={(event) => {
					event.preventDefault();
					if (live) onPress({ restart: true });
				}}
				className="rounded-2xl bg-white/15 px-4 py-2 text-base font-bold shadow transition active:scale-95"
			>
				↩︎ Start this one again
			</button>
		</div>
	);
}

/**
 * Number keys pick tubes, for whoever is playing on a laptop.
 *
 * The tubes are already buttons, so a keyboard can tab to them — but this game
 * is a lot of quick alternating taps, and tabbing across eight tubes to reach
 * the one you want is not the same game.
 */
function useNumberKeys(tubes: number, onPress: (input: SorterInput) => void, live: boolean) {
	useEffect(() => {
		if (!live) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "r") {
				event.preventDefault();
				onPress({ restart: true });
				return;
			}
			const tube = Number.parseInt(event.key, 10);
			if (!Number.isInteger(tube) || tube < 1 || tube > tubes) return;
			event.preventDefault();
			onPress({ tap: tube - 1 });
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [tubes, onPress, live]);
}
